import { useMemo, useState } from 'react';
import { C } from '../../theme';
import { METRIC_SCHEMA, emptyRawInputs } from '../../qbr/metrics';
import { gradeMetric, gradeColor, QBR_METRICS } from '../../qbr/grading';
import { fetchAllModulesQbrData } from '../../qbr/fmxData';

// Step 2 of the QBR flow — report-style review screen.
//
// Single scrollable page. Three section types, top to bottom:
//   1. Tenant-wide section — adoption only (one number for the whole site).
//   2. Overall section — the 5 module-level metrics, pooled across modules.
//   3. One section per work-request module — the same 5 metrics scoped to
//      that module.
//
// Each metric is a one-line row: label · formatted value · grade. Click
// a row to expand it inline for editing; click again (or the Done button)
// to collapse. Rows with no auto-filled data are greyed and show the
// reason instead of the value.
//
// State model: one rawInputs blob per section (tenant + overall + each
// module). Edits to a module section don't propagate to Overall; the
// FMXer reviews each in isolation.
//
// Output (onContinue): {
//   rawInputs/values/grades — convenience copy of the Overall+Tenant view,
//   byModule[slug] = { rawInputs, values, grades, moduleLabel },
//   overall = { rawInputs, values, grades },
//   tenant  = { rawInputs, values, grades },
//   moduleSlugs
// }
const OVERALL = '__overall__';

// All possible module-level metrics. Which ones apply to a given scope
// depends on whether that scope has a work-request side, a work-task
// side, or both — see scopeMetrics() below.
const ALL_MODULE_METRICS = ['completion_rate', 'response_time', 'backlog_aging', 'planned_vs_reactive', 'pm_compliance'];

// Metrics applicable to a SINGLE scope (slide-level / per-module
// section). planned_vs_reactive is intentionally absent — it's an
// Overall-only metric that pools every completed WR and PM across
// every module, no scope pairing required. See overallMetrics()
// for the Overall section's list.
//   WR only  → completion, response, backlog
//   WT only  → pm_compliance
//   both     → all of the above
function scopeMetrics({ hasWR, hasWT }) {
  const out = [];
  if (hasWR) out.push('completion_rate', 'response_time', 'backlog_aging');
  if (hasWT) out.push('pm_compliance');
  return out;
}

// Overall section's metric list. Includes the module rollups plus
// the tenant-wide Adoption metrics — moved out of a separate
// 'Tenant-wide' card and into Overall so the FMXer sees one cohesive
// scorecard.
function overallMetrics({ hasWR, hasWT, hasPowerUser }) {
  const out = [];
  if (hasWR) out.push('completion_rate', 'response_time', 'backlog_aging');
  if (hasWR || hasWT) out.push('planned_vs_reactive');
  if (hasWT) out.push('pm_compliance');
  out.push('adoption');
  if (hasPowerUser) out.push('adoption_power');
  return out;
}

export default function QbrDataStep({ setup, project, onBack, onContinue }) {
  // Setup step lets the FMXer uncheck modules to exclude from this
  // QBR run. When the selected lists are missing (older Setup output
  // shape) we default to "include everything" for backward compat.
  const allWorkRequestModules = project?.fmx_modules?.workRequestModules || [];
  const allWorkTaskModules    = project?.fmx_modules?.workTaskModules    || [];
  const selectedWRSlugs = setup?.selectedWRSlugs
    ? new Set(setup.selectedWRSlugs)
    : new Set(allWorkRequestModules.map(m => m.slug));
  const selectedWTSlugs = setup?.selectedWTSlugs
    ? new Set(setup.selectedWTSlugs)
    : new Set(allWorkTaskModules.map(m => m.slug));
  const workRequestModules = allWorkRequestModules.filter(m => selectedWRSlugs.has(m.slug));
  const workTaskModules    = allWorkTaskModules   .filter(m => selectedWTSlugs.has(m.slug));
  const pmTargetPct = project?.card_settings?.QBR?.pmTarget ?? null;

  // Build the unified scope list — one entry per unique slug, marked with
  // which sides (WR / WT) exist. WR-only scopes come first (in configured
  // order), then any WT-only scopes appended.
  const scopes = useMemo(() => {
    const wrSlugs = new Set(workRequestModules.map(m => m.slug));
    const wtSlugs = new Set(workTaskModules.map(m => m.slug));
    const out = [];
    for (const m of workRequestModules) {
      out.push({ slug: m.slug, label: m.label, hasWR: true, hasWT: wtSlugs.has(m.slug) });
    }
    for (const m of workTaskModules) {
      if (!wrSlugs.has(m.slug)) {
        out.push({ slug: m.slug, label: m.label, hasWR: false, hasWT: true });
      }
    }
    return out;
  }, [workRequestModules, workTaskModules]);

  // Overall scope shape — applicable metrics derived from the union of
  // all per-scope shapes. hasPowerUser is set when the FMXer picked
  // any user types on Setup; toggles the adoption_power card.
  const overallShape = useMemo(() => ({
    hasPowerUser: Array.isArray(setup?.powerUserTypeIds) && setup.powerUserTypeIds.length > 0,
    hasWR: scopes.some(s => s.hasWR),
    hasWT: scopes.some(s => s.hasWT),
  }), [scopes]);

  const [inputsByScope, setInputsByScope] = useState(() => {
    const init = { [OVERALL]: emptyRawInputs() };
    for (const s of scopes) init[s.slug] = emptyRawInputs();
    return init;
  });
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [fetchResult, setFetchResult] = useState(null);
  const [editing, setEditing] = useState(null); // { scope, metric } | null
  const [showFetchDebug, setShowFetchDebug] = useState(false);

  const computedByScope = useMemo(() => {
    const out = {};
    for (const scope of Object.keys(inputsByScope)) {
      const values = {}, grades = {};
      for (const m of QBR_METRICS) {
        const def = METRIC_SCHEMA[m.key];
        const value = def.compute(inputsByScope[scope][m.key] || {});
        values[m.key] = value;
        // adoption_power has no dedicated row in qbr_grading_thresholds
        // — fall back to adoption's rubric so the grade can compute.
        const tconf = setup.thresholds?.[m.key]
          ?? (m.key === 'adoption_power' ? setup.thresholds?.adoption : undefined);
        grades[m.key] = value ? gradeMetric(m.key, value, tconf) : null;
      }
      out[scope] = { values, grades };
    }
    return out;
  }, [inputsByScope, setup.thresholds]);

  // Overall letter grade — mean of the metric grades that have values
  // (all metrics in Overall — adoption + adoption_power live here now
  // since the Tenant section was folded in).
  const headlineGrade = useMemo(() => {
    const grades = [
      ...ALL_MODULE_METRICS.map(k => computedByScope[OVERALL]?.grades?.[k]),
      computedByScope[OVERALL]?.grades?.adoption,
      computedByScope[OVERALL]?.grades?.adoption_power,
    ].filter(Boolean);
    if (grades.length === 0) return null;
    const pts = { A: 4, B: 3, C: 2, D: 1, F: 0 };
    const avg = grades.reduce((a, g) => a + (pts[g] ?? 0), 0) / grades.length;
    if (avg >= 3.5) return 'A';
    if (avg >= 2.5) return 'B';
    if (avg >= 1.5) return 'C';
    if (avg >= 0.5) return 'D';
    return 'F';
  }, [computedByScope]);

  function setField(scope, metric, key, raw) {
    setInputsByScope(prev => ({
      ...prev,
      [scope]: {
        ...prev[scope],
        [metric]: { ...(prev[scope][metric] || {}), [key]: raw },
      },
    }));
  }

  async function handleFetchFromFmx() {
    if (scopes.length === 0) {
      setFetchError('No work-request or work-task modules configured on this project. Set them up in Settings.');
      return;
    }
    setFetching(true);
    setFetchError(null);
    setFetchResult(null);
    try {
      const result = await fetchAllModulesQbrData({
        projectId: project.id,
        workRequestModules,
        workTaskModules,
        scheduleRequestModules: project?.fmx_modules?.scheduleRequestModules || [],
        powerUserTypeIds: setup?.powerUserTypeIds || null,
        pmTargetPct,
        periodStart: setup.periodStart,
        periodEnd: setup.periodEnd,
      });
      setFetchResult(result);

      setInputsByScope(prev => {
        const next = { ...prev };
        // Adoption + adoption_power live on the Overall scope now;
        // the old Tenant scope was folded into Overall.
        next[OVERALL] = mergePrefill(
          prev[OVERALL], result.overall.metrics,
          result.adoption, result.adoptionPower,
        );
        for (const slug of Object.keys(result.byModule)) {
          next[slug] = mergePrefill(prev[slug], result.byModule[slug].metrics, null);
        }
        return next;
      });
    } catch (e) {
      setFetchError(`Fetch failed: ${e.message}`);
    } finally {
      setFetching(false);
    }
  }

  function handleContinue() {
    const overallComp = computedByScope[OVERALL] || { values: {}, grades: {} };
    const fr = fetchResult || {};
    const frOverall = fr.overall || {};
    const byModule = {};
    for (const s of scopes) {
      const c = computedByScope[s.slug];
      if (!c) continue;
      byModule[s.slug] = {
        rawInputs: inputsByScope[s.slug],
        values: c.values, grades: c.grades,
        moduleLabel: s.label,
        hasWR: s.hasWR, hasWT: s.hasWT,
        rawCounts: fr.byModule?.[s.slug]?.rawCounts,
      };
    }

    onContinue({
      rawInputs: inputsByScope[OVERALL],
      values:    overallComp.values,
      grades:    overallComp.grades,
      headlineGrade,
      byModule,
      overall: {
        rawInputs: inputsByScope[OVERALL],
        values: overallComp.values,
        grades: overallComp.grades,
        statusBreakdown: frOverall.statusBreakdown,
        topBuildings:    frOverall.topBuildings,
        topRequestors:   frOverall.topRequestors,
        topOverduePms:   frOverall.topOverduePms,
        topEquipment:    frOverall.topEquipment,
        ageBuckets:      frOverall.ageBuckets,
      },
      // tenant kept as a backward-compat mirror for consumers that
      // still read dataResult.tenant.values.adoption (narrative.js,
      // buildQbrDeck.js). Same values as overall — Tenant scope was
      // folded into Overall.
      tenant:   { rawInputs: inputsByScope[OVERALL], values: overallComp.values, grades: overallComp.grades },
      prior:         fr.prior,
      trend:         fr.trend,
      scheduling:    fr.scheduling,
      invoicing:     fr.invoicing,
      adoptionDebug: fr.adoptionDebug,
      moduleSlugs: scopes.map(s => s.slug),
    });
  }

  const anyValueEntered = Object.values(computedByScope).some(c =>
    Object.values(c.values).some(v => v != null)
  );

  return (
    <div>
      {/* Headline strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 11, color: C.textMid, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
            Quarterly Business Review
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, marginTop: 2 }}>
            {setup.quarterLabel}
            {project?.name && <span style={{ color: C.textMid, fontWeight: 500 }}> · {project.name}</span>}
          </div>
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
            Graded against the {setup.thresholdMode === 'custom' ? 'custom' : 'standard'} thresholds.
            Click any row to edit.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {headlineGrade && <HeadlineGrade grade={headlineGrade} />}
          <button
            onClick={handleFetchFromFmx}
            disabled={fetching || !project?.fmx_connection_verified || scopes.length === 0}
            title={!project?.fmx_connection_verified ? 'Project has no verified FMX connection' : ''}
            style={fetchBtnStyle(fetching, project, scopes)}
          >{fetching ? `Fetching${scopes.length > 1 ? ` (${scopes.length} modules)` : ''}…` : '↓ Fetch from FMX'}</button>
        </div>
      </div>

      {fetchError && (
        <div style={{
          padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12,
          background: C.errBg, color: C.errText, border: `1px solid ${C.errBorder}`,
        }}>{fetchError}</div>
      )}

      {fetchResult && (
        <FetchHeadline
          result={fetchResult}
          scopes={scopes}
          showDebug={showFetchDebug}
          onToggleDebug={() => setShowFetchDebug(s => !s)}
        />
      )}

      <div style={{ display: 'grid', gap: 14 }}>

        {/* Overall rollup */}
        <Section
          title="Overall"
          subtitle={scopes.length > 1 ? 'All modules combined, pooled by volume.' : 'Site totals.'}
          accent={C.orange}
        >
          {overallMetrics(overallShape).map(k => (
            <MetricRow
              key={k} metricKey={k}
              raw={inputsByScope[OVERALL][k]}
              value={computedByScope[OVERALL].values[k]}
              grade={computedByScope[OVERALL].grades[k]}
              reason={noDataReason(k, OVERALL, fetchResult, scopes)}
              editing={editing?.scope === OVERALL && editing.metric === k}
              onToggleEdit={() => setEditing(editing?.scope === OVERALL && editing.metric === k ? null : { scope: OVERALL, metric: k })}
              onChange={(input, val) => setField(OVERALL, k, input, val)}
            />
          ))}
        </Section>

        {/* Per-scope sections */}
        {scopes.map(s => (
          <Section
            key={s.slug}
            title={s.label}
            subtitle={scopeSubtitle(s)}
            accent={C.textMid}
            badge={<ModuleCounts result={fetchResult} slug={s.slug} />}
          >
            {scopeMetrics(s).map(k => (
              <MetricRow
                key={k} metricKey={k}
                raw={inputsByScope[s.slug][k]}
                value={computedByScope[s.slug].values[k]}
                grade={computedByScope[s.slug].grades[k]}
                reason={noDataReason(k, s.slug, fetchResult, scopes)}
                editing={editing?.scope === s.slug && editing.metric === k}
                onToggleEdit={() => setEditing(editing?.scope === s.slug && editing.metric === k ? null : { scope: s.slug, metric: k })}
                onChange={(input, val) => setField(s.slug, k, input, val)}
              />
            ))}
          </Section>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <button onClick={onBack} style={btnSecondary}>← Back</button>
        <button
          onClick={handleContinue}
          disabled={!anyValueEntered}
          style={{ ...btnPrimary,
            background: anyValueEntered ? C.orange : '#D1D5DB',
            cursor:     anyValueEntered ? 'pointer' : 'not-allowed' }}
        >Continue →</button>
      </div>
    </div>
  );
}

// ----- Section wrapper -----
function Section({ title, subtitle, accent, badge, children }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
        background: '#FAFBFC',
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
          <div style={{ width: 3, height: 18, borderRadius: 2, background: accent, alignSelf: 'center' }} />
          <div style={{ fontWeight: 700, color: C.navy, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11, color: C.textMid, fontStyle: 'italic', minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{subtitle}</div>
          )}
        </div>
        {badge}
      </div>
      <div style={{ display: 'grid' }}>
        {children}
      </div>
    </div>
  );
}

// ----- One metric row, with collapsed/expanded modes -----
function MetricRow({ metricKey, raw, value, grade, reason, editing, onToggleEdit, onChange }) {
  const def = METRIC_SCHEMA[metricKey];
  const hasValue = value != null;
  const muted = !hasValue;

  return (
    <div style={{
      borderBottom: `1px solid ${C.border}`,
      background: editing ? '#FFFDF7' : 'transparent',
      transition: 'background 0.12s',
    }}>
      <div
        onClick={onToggleEdit}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px', cursor: 'pointer',
          color: muted ? C.textLight : C.textDark,
        }}
      >
        <div style={{
          flex: '0 0 180px', fontSize: 13, fontWeight: 600,
          color: muted ? C.textLight : C.navy,
        }}>{def.label}</div>
        <div style={{ flex: 1, fontSize: 12, fontStyle: muted ? 'italic' : 'normal' }}>
          {hasValue ? def.formatValue(value) : (reason || 'No data — click to add manually')}
        </div>
        <GradeBadge grade={grade} />
        <div style={{ fontSize: 14, color: C.textLight, width: 16, textAlign: 'center' }}>
          {editing ? '▾' : '▸'}
        </div>
      </div>
      {editing && (
        <div style={{
          padding: '8px 16px 14px 208px',
          borderTop: `1px dashed ${C.border}`,
          background: '#FFFDF7',
          display: 'grid', gap: 8,
        }}>
          {def.inputs.map(inp => (
            <InputRow
              key={inp.key}
              label={inp.label} unit={inp.unit} options={inp.options}
              value={raw?.[inp.key] ?? ''}
              onChange={v => onChange(inp.key, v)}
            />
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onToggleEdit} style={{
              padding: '4px 10px', fontSize: 11, fontWeight: 600,
              background: C.navy, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
            }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Reason helper -----
function noDataReason(metricKey, scope, fetchResult, scopes) {
  if (!fetchResult) return null;
  if (metricKey === 'adoption') {
    if (fetchResult.adoptionDebug?.error) return `Couldn't fetch users: ${fetchResult.adoptionDebug.error}`;
    if (fetchResult.adoptionDebug?.counts?.eligible === 0) return 'No eligible users on this site';
    if ((fetchResult.adoptionDebug?.counts?.withLoginRecord || 0) === 0) return 'No users have a login record';
    return null;
  }
  if (metricKey === 'adoption_power') {
    if (fetchResult.adoptionDebug?.error) return `Couldn't fetch users: ${fetchResult.adoptionDebug.error}`;
    const pu = fetchResult.adoptionDebug?.powerUser;
    if (!pu) return 'No power-user types selected on Setup';
    if ((pu.counts?.eligible || 0) === 0) return 'No eligible users match the selected power-user types';
    if ((pu.counts?.withLoginRecord || 0) === 0) return 'No power users have a login record';
    return null;
  }
  // Per-module scope
  if (scope !== OVERALL) {
    const m = fetchResult.byModule?.[scope];
    if (!m) return 'Module not in fetch result';
    if (metricKey === 'completion_rate' && m.hasWR && m.rawCounts.inPeriod === 0) {
      return 'No work-requests created in this period';
    }
    if (metricKey === 'response_time' && m.hasWR && m.rawCounts.inPeriod === 0) {
      return 'No work-requests in this period to time';
    }
    if (metricKey === 'backlog_aging' && m.hasWR
        && (m.rawCounts.openPulled ?? m.rawCounts.pulled) === 0) {
      return 'No open work-requests in this module';
    }
    if (metricKey === 'pm_compliance' && m.hasWT && m.rawCounts.pmInPeriod === 0) {
      return 'No PM occurrences scheduled in this period';
    }
    // planned_vs_reactive is intentionally NOT rendered per-scope.
    // No reason to compute.
    return null;
  }
  // Overall scope
  if (scope === OVERALL) {
    const anyWR = scopes.some(s => s.hasWR);
    const anyWT = scopes.some(s => s.hasWT);
    const o = fetchResult.overall;
    if ((metricKey === 'completion_rate' || metricKey === 'response_time' || metricKey === 'backlog_aging') && !anyWR) {
      return 'No work-request modules configured';
    }
    if (metricKey === 'pm_compliance' && !anyWT) {
      return 'No work-task modules configured';
    }
    if (metricKey === 'planned_vs_reactive' && !anyWR && !anyWT) {
      return 'No work-request or work-task modules configured';
    }
    if (metricKey === 'completion_rate' && (o.rawCounts.inPeriod || 0) === 0) {
      return 'No work-requests in period across any module';
    }
  }
  return null;
}

function scopeSubtitle(scope) {
  if (scope.hasWR && scope.hasWT) {
    return `${scope.slug}-requests · paired with ${scope.slug}/occurrences`;
  }
  if (scope.hasWR) {
    return `${scope.slug}-requests · no paired PM module`;
  }
  return `${scope.slug}/occurrences only · no paired work-request module`;
}

// ----- Per-module counts badge -----
function ModuleCounts({ result, slug }) {
  if (!result) return null;
  const m = result.byModule?.[slug];
  if (!m) return null;
  return (
    <div style={{ fontSize: 11, color: C.textMid, whiteSpace: 'nowrap' }}>
      {m.rawCounts.inPeriod}/{m.rawCounts.pulled} WOs
      {m.rawCounts.pmPulled > 0 && (
        <> · {m.rawCounts.pmInPeriod}/{m.rawCounts.pmPulled} PMs</>
      )}
    </div>
  );
}

// ----- Headline grade circle -----
function HeadlineGrade({ grade }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: gradeColor(grade), color: '#fff', fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      }}>{grade}</div>
      <div style={{ fontSize: 9, color: C.textMid, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        Overall
      </div>
    </div>
  );
}

// ----- Fetch headline (collapsible debug) -----
function FetchHeadline({ result, scopes, showDebug, onToggleDebug }) {
  const { overall, byModule, adoptionDebug, errors, warnings } = result;
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 6, fontSize: 12, marginBottom: 12,
      background: C.okBg, color: C.okText, border: `1px solid ${C.okBorder}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div>
          Pulled <strong>{scopes.length}</strong> module{scopes.length === 1 ? '' : 's'} ·{' '}
          <strong>{overall.rawCounts.pulled || 0}</strong> WOs ({overall.rawCounts.inPeriod || 0} in period)
          {overall.rawCounts.pmPulled > 0 && (
            <> · <strong>{overall.rawCounts.pmPulled}</strong> PMs ({overall.rawCounts.pmInPeriod} in period)</>
          )}
          {adoptionDebug?.counts && (
            <> · <strong>{adoptionDebug.counts.eligible}</strong> eligible users</>
          )}.
        </div>
        <button onClick={onToggleDebug} style={{
          fontSize: 11, padding: '2px 8px', borderRadius: 4,
          background: 'transparent', color: C.textMid,
          border: `1px solid ${C.border}`, cursor: 'pointer',
        }}>{showDebug ? 'Hide details' : 'Show details'}</button>
      </div>
      {showDebug && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.border}`, display: 'grid', gap: 4 }}>
          {Object.entries(byModule).map(([slug, m]) => (
            <div key={slug} style={{ color: C.textMid }}>
              · <strong>{m.moduleLabel}</strong>: {m.rawCounts.pulled} WOs
              ({m.rawCounts.inPeriod} in period)
              {m.rawCounts.pmPulled > 0 && `, ${m.rawCounts.pmInPeriod}/${m.rawCounts.pmPulled} PMs`}
              {m.dateRange?.earliest && ` · earliest WO ${m.dateRange.earliest}, latest ${m.dateRange.latest}`}
            </div>
          ))}
          {Object.keys(errors || {}).length > 0 && (
            <div style={{ color: C.errText }}>
              Errors: {Object.entries(errors).map(([s, e]) => `${s}: ${e}`).join('; ')}
            </div>
          )}
          {Object.keys(warnings || {}).length > 0 && (
            <div style={{ color: C.warnText }}>
              Warnings: {Object.entries(warnings).map(([s, w]) => `${s}: ${w}`).join('; ')}
            </div>
          )}
          {adoptionDebug?.counts && (
            <div style={{ color: C.textMid }}>
              Users: {adoptionDebug.counts.totalPulled} pulled · {adoptionDebug.counts.eligible} eligible ·{' '}
              {adoptionDebug.counts.withLoginRecord} have a login record ·{' '}
              {adoptionDebug.counts.active30} in 30d · {adoptionDebug.counts.active90} in 90d
              {adoptionDebug.mostRecentLogin && ` · most recent login ${adoptionDebug.mostRecentLogin}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function mergePrefill(prev, metrics, adoption, adoptionPower) {
  const base = prev || emptyRawInputs();
  const next = { ...base };
  if (metrics?.completion_rate) {
    next.completion_rate = {
      completed: String(metrics.completion_rate.completed),
      total:     String(metrics.completion_rate.total),
    };
  }
  if (metrics?.response_time) {
    const rt = metrics.response_time;
    const fmt = v => v != null ? String(round1(v)) : '';
    next.response_time = {
      ...base.response_time,
      responseDays:       fmt(rt.responseDays),
      responseMeanDays:   fmt(rt.responseMeanDays),
      assignmentDays:     fmt(rt.assignmentDays),
      assignmentMeanDays: fmt(rt.assignmentMeanDays),
      resolutionDays:     fmt(rt.resolutionDays),
      resolutionMeanDays: fmt(rt.resolutionMeanDays),
    };
  }
  if (metrics?.backlog_aging) {
    const b = metrics.backlog_aging;
    next.backlog_aging = {
      overdue30dPct:   b.overdue30dPct   != null ? String(b.overdue30dPct)   : '',
      oldestDays:      b.oldestDays      != null ? String(b.oldestDays)      : '',
      openCount:       b.openCount       != null ? String(b.openCount)       : '',
      overdue30dCount: b.overdue30dCount != null ? String(b.overdue30dCount) : '',
    };
  }
  if (metrics?.planned_vs_reactive) {
    next.planned_vs_reactive = {
      planned:  String(metrics.planned_vs_reactive.planned),
      reactive: String(metrics.planned_vs_reactive.reactive),
    };
  }
  if (metrics?.pm_compliance) {
    const c = metrics.pm_compliance;
    next.pm_compliance = {
      onTimePct:  c.onTimePct  != null ? String(c.onTimePct)  : '',
      anyTimePct: c.anyTimePct != null ? String(c.anyTimePct) : '',
      targetPct:  c.targetPct  != null ? String(c.targetPct)  : '',
    };
  }
  if (adoption) {
    next.adoption = {
      ...base.adoption,
      active30Pct: adoption.active30Pct != null ? String(adoption.active30Pct) : '',
      active90Pct: adoption.active90Pct != null ? String(adoption.active90Pct) : '',
    };
  }
  if (adoptionPower) {
    next.adoption_power = {
      ...base.adoption_power,
      active30Pct: adoptionPower.active30Pct != null ? String(adoptionPower.active30Pct) : '',
      active90Pct: adoptionPower.active90Pct != null ? String(adoptionPower.active90Pct) : '',
    };
  }
  return next;
}

function round1(n) { return Math.round(n * 10) / 10; }

function InputRow({ label, unit, options, value, onChange }) {
  if (unit === 'select' && options) {
    return (
      <label style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8 }}>
        <span style={inputLabel}>{label}</span>
        <select value={value} onChange={e => onChange(e.target.value)} style={inputSmall}>
          <option value="">—</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 8 }}>
      <span style={inputLabel}>{label}</span>
      <input
        type="number" inputMode="decimal" min="0"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={inputSmall}
      />
      <span style={{ fontSize: 10, color: C.textLight, textTransform: 'uppercase' }}>{unit}</span>
    </label>
  );
}

function GradeBadge({ grade }) {
  if (!grade) return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%',
      background: '#F3F4F6', color: C.textLight, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, flexShrink: 0,
    }}>—</div>
  );
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%',
      background: gradeColor(grade), color: '#fff', fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.12)', flexShrink: 0,
    }}>{grade}</div>
  );
}

function fetchBtnStyle(fetching, project, scopes) {
  const disabled = fetching || !project?.fmx_connection_verified || scopes.length === 0;
  return {
    padding: '8px 14px', fontSize: 12, fontWeight: 600,
    background: '#fff', color: C.navy,
    border: `1px solid ${C.border}`, borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  };
}

const inputLabel = { fontSize: 12, color: '#374151' };

const inputSmall = {
  padding: '5px 8px', fontSize: 12, borderRadius: 4,
  border: `1px solid ${C.border}`, outline: 'none', textAlign: 'right',
  width: 90,
};

const btnPrimary = {
  padding: '10px 24px', fontSize: 14, fontWeight: 600,
  color: '#fff', border: 'none', borderRadius: 6,
};
const btnSecondary = {
  padding: '8px 16px', fontSize: 13, fontWeight: 500,
  background: '#fff', color: C.textDark,
  border: `1px solid ${C.border}`, borderRadius: 6, cursor: 'pointer',
};
